import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, CardSubtitle, CardTitle } from "reactstrap";
import Chart from "react-apexcharts";
import DateRangeSelector from "./DateRangeSelector";
import {
  formatDateForPayload,
  getCurrentWeekRange,
} from "../Utils/UtilityFunctions";
import { getReservationSummary } from "../../slices/EVDashboard/ReservationSummary/thunk";

// Custom Skeleton component
const Skeleton = ({ width, height, className = "", style = {} }) => (
  <div
    className={`skeleton ${className}`}
    style={{
      width,
      height,
      backgroundColor: "#e0e0e0",
      borderRadius: "4px",
      animation: "skeleton-loading 1.5s infinite ease-in-out",
      ...style,
    }}
  />
);

export default function ReservationsChart() {
  const currentWeekRange = getCurrentWeekRange();
  const [dateRange, setDateRange] = useState(currentWeekRange);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const reservationSummary = useSelector(
    (state) => state.ReservationSummary.reservationSummary
  );
  const stationSummary = reservationSummary?.stationSummary || [];

  // Show skeleton loader while data is loading
  const isLoading = !reservationSummary;

  // Calculate dynamic max value based on data
  const maxCount = Math.max(
    ...stationSummary.map((item) => item?.count || 0),
    0
  );
  const dynamicMax = maxCount > 0 ? Math.ceil(maxCount * 1.1) : 100;

  const options = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
      fontFamily: "system-ui, -apple-system, sans-serif",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "40%",
        distributed: false,
      },
    },
    colors: ["#1D4163"],
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: stationSummary.map((item) => item?.stationId || "N/A"),
      labels: {
        rotate: -45,
        rotateAlways: true,
        trim: false,
        hideOverlappingLabels: false,
        style: {
          colors: "#9ca3af",
          fontSize: "12px",
          whiteSpace: "normal",
        },
        formatter: function (value) {
          if (!value) return "";
          // Split long station IDs into multiple lines (every 15 characters)
          if (value.length > 15) {
            const chunks = value.match(/.{1,15}/g) || [value];
            return chunks.join("\n");
          }
          return value;
        },
      },
      axisBorder: {
        color: "#e5e7eb",
      },
      axisTicks: {
        color: "#e5e7eb",
      },
    },
    yaxis: {
      min: 0,
      max: dynamicMax,
      tickAmount: 4,
      labels: {
        style: {
          colors: "#9ca3af",
          fontSize: "14px",
        },
        formatter: (value) => Math.round(value),
      },
      title: {
        text: "Reservation Count",
        style: {
          fontSize: "12px",
          fontWeight: 400,
          color: "#808080",
        },
      },
    },
    grid: {
      borderColor: "#f0f0f0",
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: "bottom",
      horizontalAlign: "center",
      markers: {
        width: 16,
        height: 16,
        radius: 2,
      },
      itemMargin: {
        horizontal: 10,
      },
      fontSize: "16px",
      fontWeight: 500,
    },
  };

  const series = [
    {
      name: "Reservations",
      data: stationSummary.map((item) => item?.count || 0),
    },
  ];

  const handleDateRangeChange = (dateRangeData) => {
    const { startDate, endDate, type } = dateRangeData;
    const dateRange = {
      period: type,
      from: formatDateForPayload(startDate) || currentWeekRange.from,
      to: formatDateForPayload(endDate) || currentWeekRange.to,
    };
    setDateRange(dateRange);
  };

  useEffect(() => {
    if (dateRange) {
      dispatch(getReservationSummary(setLoading, dateRange));
    }
  }, [dateRange]);

  return (
    <>
      <style>{`
        @keyframes skeleton-loading {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.4;
          }
          100% {
            opacity: 1;
          }
        }
        
        .skeleton {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
        }
      `}</style>

      <Card style={{ border: "1px solid #0000001A", borderRadius: "10px" }}>
        <CardBody className="p-4">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start mb-3">
            <div>
              <CardTitle
                tag="h3"
                className="mb-2"
                style={{
                  fontSize: "18px",
                  color: "#0A0A0A",
                  fontWeight: "400",
                }}
              >
                Reservations by Charging Station
              </CardTitle>
              {/* <CardSubtitle
                style={{
                  fontSize: "14px",
                  color: "#4A5565",
                  fontWeight: "400",
                }}
              >
                Total reservations by station
              </CardSubtitle> */}
            </div>
            {/* <div
              style={{
                fontSize: "14px",
                color: "#4A5565",
                fontWeight: "400",
              }}
            >
              Trailing last 3 months
            </div> */}
            <div className="flex-shrink-0">
              <DateRangeSelector onDateRangeChange={handleDateRangeChange} />
            </div>
          </div>

          {isLoading ? (
            <div className="mt-4">
              {/* Y-axis labels and chart container */}
              <div className="d-flex">
                <div className="me-3" style={{ width: "60px" }}>
                  {[...Array(5)].map((_, i) => (
                    <div key={i} style={{ marginBottom: "40px" }}>
                      <Skeleton width="40px" height="10px" />
                    </div>
                  ))}
                </div>

                {/* Chart bars skeleton */}
                <div className="flex-grow-1">
                  <div
                    className="d-flex align-items-end justify-content-around gap-2"
                    style={{ height: "240px" }}
                  >
                    {[150, 220, 180, 260, 190, 240, 170, 200].map(
                      (height, i) => (
                        <Skeleton
                          key={i}
                          width="40px"
                          height={`${height}px`}
                          style={{
                            borderRadius: "4px 4px 0 0",
                            animationDelay: `${i * 0.1}s`,
                          }}
                        />
                      )
                    )}
                  </div>

                  {/* X-axis labels skeleton */}
                  <div className="d-flex justify-content-around mt-3 gap-2">
                    {[...Array(8)].map((_, i) => (
                      <Skeleton
                        key={i}
                        width="30px"
                        height="16px"
                        style={{ transform: "rotate(-45deg)" }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Legend skeleton */}
              <div className="d-flex justify-content-center mt-4">
                <Skeleton width="150px" height="12px" />
              </div>
            </div>
          ) : (
            <Chart options={options} series={series} type="bar" height={300} />
          )}
        </CardBody>
      </Card>
    </>
  );
}
